import { Link, useNavigate, useParams } from "react-router-dom";
import { Checkmark, Edit, Plus } from "assets/icons";
import {
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { Resource, ResourceType } from "shared/types/resource";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
} from "components/index";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  InsuranceIssue,
  InsuranceIssueRole,
  InsuranceIssueStatus,
  VisitationRequiredWithInsuranceExpert,
} from "shared/types/insurance";
import {
  Building,
  Buildings,
  Eye,
  ListBullets,
  ListChecks,
  MoneyWavy,
  PencilSimpleLine,
  PiggyBank,
  QuestionMark,
  StackSimple,
  TextAlignLeft,
  User,
  UsersThree,
  WarningOctagon,
} from "@phosphor-icons/react";
import { createPatchObject } from "shared/utils/patch";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { FileLabel, FileLog } from "shared/types/file";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";

const InsuranceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [insuranceIssue, setInsuranceIssue] = useState<InsuranceIssue>();
  const [editedInsuranceIssue, setEditedInsuranceIssue] =
    useState<InsuranceIssue>();
  const { session } = useSession();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [building, setBuilding] = useState<BuildingDTO>();
  const [unit, setUnit] = useState<UnitDTO>();
  const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const { user, isLoaded } = useUser();

  useEffect(() => {
    if (!id && isLoaded) {
      setIsLoading(false);
      // No ID => Creating a new insurance issue

      const newInsuranceIssue: InsuranceIssue = {
        title: "",
        actionsTaken: "",
        complexities: "",
        insuranceIssueStatus: InsuranceIssueStatus.GATHERING_INFO,
        visitationRequiredWithInsuranceExpert:
          VisitationRequiredWithInsuranceExpert.UNKNOWN,
        role: InsuranceIssueRole.UNKNOWN,
        description: "",
        resolvedInfo: "",
        insuranceCompanyYallyResourceId: null,
        insuranceCompanyCounterpartyResourceId: null,
        counterpartyResourceId: null,
        askedCompensation: "",
        receivedCompensation: "",
        fileLogIds: [],
        fileLogs: [],
        user: user as any,
      };

      setInsuranceIssue(newInsuranceIssue);
      setEditedInsuranceIssue(newInsuranceIssue);

      setEditMode(true); // Start in edit mode
    }
  }, [id, isLoaded]);

  const fetchInsuranceIssue = async () => {
    if (id) {
      try {
        const data = await fetchWithAuth(
          `${API_URL}/insurance/file/${id}`,
          { method: "GET" },
          session,
        );
        if (!data.ok) {
          throw new Error("Network response was not ok");
        }
        const i: any = await data.json();
        setInsuranceIssue(i);
        setEditedInsuranceIssue(i);
        setBuilding(i.building);
        setUnit(i.unit);
      } catch (error) {
        console.error("Failed to fetch insurance issue: ", error);
        toast("Error bij laden van verzekeringsdossier");
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (id) {
      fetchInsuranceIssue();
    }
  }, [id]);

  useEffect(() => {
    // Listener for Escape key to exit edit mode
    const handleKeydown = (e: KeyboardEvent) => {
      if (editMode && e.key === "Escape") {
        toggleEditMode();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      // Cleanup event listener
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [insuranceIssue, editMode]);

  const toggleEditMode = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedInsuranceIssue(insuranceIssue);
    }
  };

  const isValidInsuranceIssue = (issue: InsuranceIssue) => {
    return (
      issue.title &&
      issue.description &&
      (issue.buildingId || issue.unitId || issue.building || issue.unit)
    );
  };

  const handleOpenAddInsuranceFileDocumentModal = () => {
    setBuilding(insuranceIssue?.building);
    setUnit(insuranceIssue?.unit);
    setFileModalOpen(true);
  };

  const handleFileModalSave = async (fileLogData: any) => {
    const newInsuranceIssueFileLogs: FileLog[] =
      fileLogData.customData?.savedInsuranceFile.fileLogs;
    const newInsuranceIssueFileLogIds: number[] =
      fileLogData.customData?.savedInsuranceFile.fileLogIds;
    if (newInsuranceIssueFileLogs) {
      setEditedInsuranceIssue((prev: any) => ({
        ...prev,
        fileLogs: newInsuranceIssueFileLogs,
        fileLogIds: newInsuranceIssueFileLogIds,
      }));
    }

    handleFileModalClose();
  };

  const handleFileModalClose = () => {
    setBuilding(undefined);
    setUnit(undefined);
    setFileModalOpen(false);
  };

  const saveInsuranceIssueUpdates = async () => {
    if (!editedInsuranceIssue || !isValidInsuranceIssue(editedInsuranceIssue)) {
      toast.error(
        "Het dossier heeft minstens een titel nodig, een gebouw en een korte beschrijving van het schadegeval.",
      );
      return;
    }

    console.log(editedInsuranceIssue);

    const updatedInsuranceIssue = {
      ...editedInsuranceIssue,
      user: user as any,
    };

    const patchObject = createPatchObject(
      insuranceIssue,
      updatedInsuranceIssue,
    );

    if (Object.keys(patchObject).length === 0) {
      toast.info("Geen wijzigingen in verzekeringsdossier gedetecteerd");
      return;
    }

    try {
      let newInsuranceIssueData: InsuranceIssue | null = null;
      if (id) {
        // Updating an existing contact
        await fetchWithAuth(
          `${API_URL}/insurance/file/${insuranceIssue?.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patchObject),
          },
          session,
        );
        fetchInsuranceIssue();
        toast.success("Verzekeringsdossier bewaard!");

        // Navigate to the updated contact after 2 seconds
        setTimeout(() => {
          navigate(`/insurance/detail/${insuranceIssue?.id}`);
        }, 2000);
      } else {
        // Creating a new insurance issue
        // No patch object required, as we are creating a new issue
        const response = await fetchWithAuth(
          `${API_URL}/insurance/file`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedInsuranceIssue),
          },
          session,
        );
        newInsuranceIssueData = await response.json();
        // Update the insurance data, especially the ID, with the new data
        if (newInsuranceIssueData && newInsuranceIssueData.id) {
          setInsuranceIssue(newInsuranceIssueData);

          // Navigate to the newly created insurance issue after 2 seconds
          setTimeout(() => {
            if (newInsuranceIssueData) {
              navigate(`/insurance/detail/${newInsuranceIssueData.id}`);
            }
          }, 2000);
        }
      }
    } catch (error) {
      console.error("Failed to save contact: ", error);
      toast("Error saving contact!");
    } finally {
      setEditMode(false);
      setIsLoading(false);
    }
  };

  const updateInsuranceIssueInfo = (updatedFields: any) => {
    if (updatedFields.buildingId) {
      setBuilding({ id: updatedFields.buildingId });
    }
    if (updatedFields.unitId) {
      setUnit({ id: updatedFields.unitId });
    }

    // Check if the status is being changed to "Betaling ontvangen"
    if (updatedFields.insuranceIssueStatus === InsuranceIssueStatus.PAYMENT_RECEIVED && 
        editedInsuranceIssue?.insuranceIssueStatus !== InsuranceIssueStatus.PAYMENT_RECEIVED) {
      setPendingStatusChange(updatedFields.insuranceIssueStatus);
      setShowArchiveConfirmModal(true);
      return;
    }

    console.log(updatedFields);
    setEditedInsuranceIssue((prev: any) => ({ ...prev, ...updatedFields }));
  };

  const handleConfirmArchive = (shouldArchive: boolean) => {
    if (pendingStatusChange) {
      const updatedFields = { 
        insuranceIssueStatus: pendingStatusChange,
        ...(shouldArchive && { archived: true })
      };
      setEditedInsuranceIssue((prev: any) => ({ ...prev, ...updatedFields }));
    }
    setShowArchiveConfirmModal(false);
    setPendingStatusChange(null);
  };

  return (
    <>
      <div className="z-10 mb-10">
        {!isLoading ? (
          <div className="flex flex-col gap-8">
            <header className="flex items-center justify-between bg-blue-300">
              <div className="flex flex-col gap-1">
                {insuranceIssue?.building && (
                  <Link
                    to={`/building/${insuranceIssue?.building?.id}`}
                    className="text-lg text-neutral-secondary-mid"
                  >
                    <Chip
                      className="gap-1"
                      startContent={<Building weight="duotone" size={14} />}
                      classNames={{
                        base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                        content: "px-0 font-medium",
                      }}
                    >
                      {insuranceIssue?.building?.id}
                    </Chip>
                  </Link>
                )}
                <p className="text-lg font-medium">
                  {insuranceIssue?.title || "Nieuw verzekeringsdossier"}
                </p>
              </div>
              <div className="flex gap-2">
                {editMode ? (
                  <ButtonNew
                    variant="primaryMoss"
                    onClick={saveInsuranceIssueUpdates}
                    icon={<Checkmark />}
                    label="Bewaren"
                    isDisabled={isLoading}
                  />
                ) : (
                  <ButtonNew
                    variant="secondaryNeutral"
                    onClick={toggleEditMode}
                    icon={<Edit />}
                    label="Bewerken"
                  />
                )}
              </div>
            </header>
            <div className="flex w-full flex-col gap-8 rounded-lg bg-white p-4 shadow-md">
              <section className="flex flex-col gap-4">
                <header className="flex items-center gap-1.5 text-lg font-medium">
                  <ListBullets weight="duotone" size={20} />
                  Algemeen
                </header>
                <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-md border border-neutral-primary/10">
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <PencilSimpleLine weight="duotone" size={18} />
                        <p>
                          Titel dossier (incl. referentie indien van toepassing)
                        </p>
                      </header>
                      <EditableInputComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.title as string}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ title: value })
                        }
                        label="Geef het dossier een titel"
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <TextAlignLeft weight="duotone" size={18} />
                        <p>Beschrijving van het schadegeval</p>
                      </header>
                      <EditableTextareaComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.description || ""}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ description: value })
                        }
                        label="Voer een beschrijving in"
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <Buildings weight="duotone" size={18} />
                        <p>Gebouw</p>
                      </header>
                      <SearchComponent
                        editMode={editMode}
                        label="Selecteer gebouw"
                        resourceFilter={[ResourceType.BUILDING]}
                        initialValue={
                          editedInsuranceIssue?.buildingId ||
                          editedInsuranceIssue?.building?.id ||
                          ""
                        }
                        onSelect={(resource) =>
                          updateInsuranceIssueInfo({
                            buildingId: resource.resourceId,
                          })
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <StackSimple weight="duotone" size={18} />
                        <p>Unit</p>
                      </header>
                      <SearchComponent
                        editMode={editMode}
                        label="Selecteer unit"
                        resourceFilter={[ResourceType.UNIT]}
                        initialValue={
                          editedInsuranceIssue?.unitId ||
                          editedInsuranceIssue?.unit?.id ||
                          ""
                        }
                        onSelect={(resource) =>
                          updateInsuranceIssueInfo({
                            unitId: resource.resourceId,
                          })
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <User weight="duotone" size={18} />
                        <p>Actuele status dossier</p>
                      </header>
                      <EditableSelectComponent
                        editMode={editMode}
                        label="Status"
                        isLoading={false}
                        options={Object.values(InsuranceIssueStatus).map(
                          (status) => ({ label: status, value: status }),
                        )}
                        value={
                          editedInsuranceIssue?.insuranceIssueStatus ||
                          InsuranceIssueStatus.GATHERING_INFO
                        }
                        onChange={(value) =>
                          updateInsuranceIssueInfo({
                            insuranceIssueStatus: value,
                          })
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <User weight="duotone" size={18} />
                        <p>
                          Moet er een bezoek worden ingepland met de
                          verzekeringsexpert?
                        </p>
                      </header>
                      <EditableSelectComponent
                        label="Bezoek door expert vereist"
                        editMode={editMode}
                        isLoading={false}
                        options={Object.values(
                          VisitationRequiredWithInsuranceExpert,
                        ).map((status) => ({ label: status, value: status }))}
                        value={
                          editedInsuranceIssue?.visitationRequiredWithInsuranceExpert ||
                          VisitationRequiredWithInsuranceExpert.UNKNOWN
                        }
                        onChange={(value) =>
                          updateInsuranceIssueInfo({
                            visitationRequiredWithInsuranceExpert: value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <User weight="duotone" size={18} />
                        <p>
                          Zijn we de begunstigde of de aansprakelijke partij?
                        </p>
                      </header>
                      <EditableSelectComponent
                        label="Zijn we de begunstigde of de aansprakelijke partij?"
                        editMode={editMode}
                        isLoading={false}
                        options={Object.values(InsuranceIssueRole).map(
                          (role) => ({
                            label: role,
                            value: role,
                          }),
                        )}
                        value={
                          editedInsuranceIssue?.role ||
                          InsuranceIssueRole.UNKNOWN
                        }
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ role: value })
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <ListChecks weight="duotone" size={18} />
                        <p>Welke acties werden er reeds genomen?</p>
                      </header>
                      <EditableTextareaComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.actionsTaken || ""}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ actionsTaken: value })
                        }
                        label="Voer een analyse in"
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <WarningOctagon weight="duotone" size={18} />
                        <p>
                          Zijn er complexiteiten of moeilijkheden in dit
                          dossier? (vaststellen van schade, houding tegenpartij,
                          ...)
                        </p>
                      </header>
                      <EditableTextareaComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.complexities || ""}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ complexities: value })
                        }
                        label="Voer een beschrijving in"
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <QuestionMark weight="duotone" size={18} />
                        <p>
                          Hoe werd het schadegeval opgelost? (indien van
                          toepassing)
                        </p>
                      </header>
                      <EditableTextareaComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.resolvedInfo || ""}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ resolvedInfo: value })
                        }
                        label="Voer een beschrijving in"
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <MoneyWavy weight="duotone" size={18} />
                        <p>Hoeveel bedraagt de compensatie die we vragen?</p>
                      </header>
                      <EditableInputComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.askedCompensation || ""}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({ askedCompensation: value })
                        }
                        label="Voer een bedrag in"
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <PiggyBank weight="duotone" size={18} />
                        <p>
                          Hoeveel bedraagt de compensatie die we zullen
                          ontvangen (indien reeds bevestigd door verzekering)
                        </p>
                      </header>
                      <EditableInputComponent
                        editMode={editMode}
                        value={editedInsuranceIssue?.receivedCompensation || ""}
                        isLoading={false}
                        onChange={(value) =>
                          updateInsuranceIssueInfo({
                            receivedCompensation: value,
                          })
                        }
                        label="Voer een bedrag in"
                      />
                    </div>
                  </div>
                </div>
              </section>
              <section className="flex flex-col gap-4">
                <header className="flex items-center gap-1.5 text-lg font-medium">
                  <UsersThree weight="duotone" size={20} />
                  Contactpersonen
                </header>
                <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-md border border-neutral-primary/10">
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <User weight="duotone" size={18} />
                        <p>Contactpersoon tegenpartij</p>
                      </header>
                      <SearchComponent
                        editMode={editMode}
                        label="Selecteer tegenpartij"
                        resourceFilter={[ResourceType.CONTACT]}
                        initialValue={
                          editedInsuranceIssue?.counterpartyResourceId || ""
                        }
                        initialValueType="id"
                        onSelect={(resource) =>
                          updateInsuranceIssueInfo({
                            counterpartyResourceId: resource.id,
                          })
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                      />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <User weight="duotone" size={18} />
                        <p>
                          Contactpersoon verzekeringsmaatschappij tegenpartij
                        </p>
                      </header>
                      <SearchComponent
                        editMode={editMode}
                        label="Selecteer tegenpartij"
                        resourceFilter={[ResourceType.CONTACT]}
                        initialValue={
                          editedInsuranceIssue?.insuranceCompanyCounterpartyResourceId ||
                          ""
                        }
                        initialValueType="id"
                        onSelect={(resource) =>
                          updateInsuranceIssueInfo({
                            insuranceCompanyCounterpartyResourceId: resource.id,
                          })
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-primary/10 sm:flex sm:divide-x sm:divide-y-0 *:w-full">
                    <div className="flex flex-col gap-1 p-3">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <User weight="duotone" size={18} />
                        <p>Contactpersoon Verzekeringsmaatschappij Yally</p>
                      </header>
                      <SearchComponent
                        editMode={editMode}
                        label="Selecteer verzekeringsmaatschappij"
                        resourceFilter={[ResourceType.CONTACT]}
                        initialValue={
                          editedInsuranceIssue?.insuranceCompanyYallyResourceId ||
                          ""
                        }
                        initialValueType="id"
                        onSelect={(resource) =>
                          updateInsuranceIssueInfo({
                            insuranceCompanyYallyResourceId: resource.id,
                          })
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <Spinner size="lg" color="warning" />
          </div>
        )}
      </div>
      <section className="z-10 flex flex-col gap-0 rounded-lg bg-white p-4 shadow-md">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          {/* File upload logic */}
          <div className="col-span-full mt-8 flex flex-col gap-3">
            <header className="flex justify-between px-2.5">
              <div className="flex items-center gap-2">
                <Eye className="h-[18px] w-[18px]" />
                <p className="text-lg">Documenten gerelateerd aan dossier</p>
              </div>
              <ButtonNew
                onClick={handleOpenAddInsuranceFileDocumentModal}
                icon={<Plus />}
                label="Voeg document toe"
                variant="primaryMoss"
              />
            </header>
            <Table>
              <TableHeader>
                {/* Define table columns */}
                {/* Example: */}
                <TableColumn>Label</TableColumn>
                <TableColumn>Samenvatting</TableColumn>
                <TableColumn>Gerelateerde partij</TableColumn>
                <TableColumn>Documentdatum</TableColumn>
                <TableColumn>Sharepoint</TableColumn>
                {/* Other columns as needed */}
              </TableHeader>
              <TableBody>
                {(
                  (editedInsuranceIssue &&
                    editedInsuranceIssue.fileLogs &&
                    editedInsuranceIssue.fileLogs) ||
                  []
                ).map((fileLog: FileLog) => (
                  <TableRow key={fileLog.id}>
                    <TableCell>
                      {(fileLog.label &&
                        fileLog.label.length > 0 &&
                        fileLog.label[0]) ||
                        ""}
                    </TableCell>
                    <TableCell>{fileLog.summary}</TableCell>
                    <TableCell>
                      {(fileLog?.relations &&
                        fileLog?.relations.length > 0 &&
                        fileLog?.relations
                          .map((r: Resource) => r.name)
                          .join(", ")) ||
                        "Auteur niet ingevuld"}
                    </TableCell>
                    <TableCell>
                      {fileLog?.documentDate
                        ? (fileLog?.documentDate as any)
                        : "Datum niet ingevuld"}
                    </TableCell>
                    <TableCell>
                      {fileLog ? (
                        <a
                          href={fileLog.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Eye />
                        </a>
                      ) : (
                        "No Document"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Modal for File Upload */}
          <ModalUploadFileLog
            isOpen={fileModalOpen}
            onClose={() => setFileModalOpen(false)}
            onSave={(fileLogData) => handleFileModalSave(fileLogData)}
            building={building}
            unit={unit}
            incomingCustomData={{ insuranceFileId: editedInsuranceIssue?.id }}
            customLabels={[
              FileLabel.InsuranceFileCommunication,
              FileLabel.InsuranceFileCommunicationCounterparty,
              FileLabel.InsuranceFileExpertiseReport,
              FileLabel.InsuranceFileInfringement,
              FileLabel.InvoiceRepairInsuredDamage,
              FileLabel.OfferRepairInsuredDamage,
            ]}
          />
        </div>
      </section>

      {/* Archive Confirmation Modal */}
      <Modal 
        isOpen={showArchiveConfirmModal} 
        onClose={() => {
          setShowArchiveConfirmModal(false);
          setPendingStatusChange(null);
        }}
        classNames={{
          backdrop: "bg-neutral-primary/50 backdrop-blur-xs",
          base: "bg-brick-secondary-fine border border-neutral-primary/20",
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold text-neutral-primary">Betaling ontvangen</h3>
          </ModalHeader>
          <ModalBody>
            <p className="text-neutral-primary">
              U heeft de status gewijzigd naar "Betaling ontvangen". Dit betekent dat het dossier is opgelost.
            </p>
            <p className="text-neutral-primary font-medium">
              Wilt u dit dossier ook archiveren?
            </p>
          </ModalBody>
          <ModalFooter className="flex gap-2">
            <Button 
              variant="flat" 
              onPress={() => handleConfirmArchive(false)}
              className="bg-neutral-primary/10 text-neutral-primary hover:bg-neutral-primary hover:text-white"
            >
              Nee, niet archiveren
            </Button>
            <Button 
              onPress={() => handleConfirmArchive(true)}
              className="bg-moss-primary text-white hover:bg-moss-primary/80"
            >
              Ja, archiveren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default InsuranceDetail;
